import { cloneDeep, isArray } from "lodash";
import { OperatorType, ValueTypes } from "../../common/types/campaign";
import InputFormControl from "./InputFormControl";
import { Box, HStack, Text } from "@chakra-ui/react";
import DropdownWithSearch from "../DropdownWithSearch";
import { isArgumentMany, isBlank } from "../../common/helper/commonHelper";
import { useMemo } from "react";
import MultiValueInput from "../MultiValueInput";
import { format } from "date-fns";
import IconWithTooltip from "../IconWithTooltip";

export function MultiValueInputFields({
  value,
  filterValue,
  onChange,
  isReadOnly,
  type,
  activeErrorCheck,
  isDisabled,
}: {
  value: (string | number)[];
  filterValue: string;
  onChange: (value: string | number | string[] | number[]) => void;
  isReadOnly?: boolean;
  type: string;
  activeErrorCheck?: boolean;
  isDisabled?: boolean;
}) {
  const inputType = useMemo(
    () =>
      [OperatorType.FLOAT, OperatorType.INTEGER].includes(type as OperatorType)
        ? "number"
        : "text",
    [type]
  );

  function onInputChange(inputs: string[]) {
    if (type === OperatorType.FLOAT || type === OperatorType.INTEGER) {
      onChange(inputs.map((input) => parseFloat(input)));
    } else {
      onChange(inputs);
    }
  }

  const readOnlyString = useMemo(() => value.join(", "), [value]);
  return isReadOnly ? (
    <Text> {readOnlyString} </Text>
  ) : (
    <Box minW="300px">
      <MultiValueInput
        inputList={value.map(String)}
        onInputListChange={onInputChange}
        errorMsg={
          activeErrorCheck && !value.length ? "Inputs should not be empty" : ""
        }
        filterValue={filterValue as string}
        inputType={inputType}
        chipsContainerProps={{
          borderRadius: "2px",
        }}
        isDisabled={isDisabled}
      />
    </Box>
  );
}

export function ValueInputFields({
  value,
  validationError,
  type,
  onChange,
  isReadOnly,
  isDisabled,
}: {
  value: string | number;
  validationError?: string;
  type: string;
  onChange: (value: string | number | string[]) => void;
  isReadOnly?: boolean;
  isDisabled?: boolean;
}) {
  let jsInputType: "text" | "number" | "date" = "text";
  let displayValue = value;

  if (type === OperatorType.DATE) {
    jsInputType = "date";
    // value does not have timezone, is just date string. ex: 2023-06-01
    const dateValue = new Date(value);
    // new Date() adds browser timezone to date string, remove that
    dateValue.setMinutes(
      dateValue.getMinutes() + dateValue.getTimezoneOffset()
    );

    // change date formatting using date-fns
    displayValue = value ? format(dateValue, "MMM dd, yyyy") : "";
  } else if (type === OperatorType.INTEGER || type === OperatorType.FLOAT) {
    jsInputType = "number";
  }

  if (isReadOnly) return <Text>"{displayValue}"</Text>;
  else {
    return (
      <InputFormControl
        width="180px"
        type={jsInputType}
        value={value}
        onChange={({ target: { value: val } }) =>
          onChange(
            jsInputType === "number" && !isBlank(val) ? Number(val) : val
          )
        }
        validationMessage={validationError}
        isDisabled={isDisabled}
      />
    );
  }
}

export function ValueSelectFields({
  value,
  validationError,
  options,
  onChange,
  loading,
  isReadOnly,
  isDisabled,
  ...props
}: {
  options: { label: string; value: string }[];
  validationError?: string;
  value: string;
  onChange: (value: string) => void;
  loading?: boolean;
  isReadOnly?: boolean;
  isDisabled?: boolean;
}) {
  if (isReadOnly) {
    return (
      <Text>
        {options.find((option) => option.value === value)?.label ?? null}
      </Text>
    );
  } else {
    return (
      <DropdownWithSearch
        options={options}
        value={options.find((option) => option.value === value) ?? null}
        onChange={(option) => onChange(option?.value ?? "")}
        isSearchable={true}
        isLoading={loading}
        isInvalid={!!validationError}
        controlStyle={{
          minWidth: "100px",
          height: "32px",
          minHeight: "32px",
          borderRadius: "2px",
        }}
        id="value-select-fields"
        isDisabled={isDisabled}
        {...props}
      />
    );
  }
}

export function DynamicListValueFields({
  value,
  validationError,
  onChange,
  argumentTypes,
  argumentSelectList,
  helperText,
  isReadOnly,
  filterValue,
  noOfArguments,
  activeErrorCheck,
  isLoading = false,
  isDisabled,
}: {
  value: ValueTypes;
  validationError?: string;
  onChange: (val: ValueTypes) => void;
  argumentTypes: (string | string[])[] | null;
  argumentSelectList?: { label: string; value: string }[];
  helperText?: string | string[];
  isReadOnly?: boolean;
  filterValue?: string;
  noOfArguments?: string;
  activeErrorCheck?: boolean;
  isLoading?: boolean;
  isDisabled?: boolean;
}) {
  function onChangeHandler(
    val: string | number | string[] | number[],
    index: number
  ) {
    let dataCopy = cloneDeep(value);
    if (typeof val === "string" || typeof val === "number") {
      dataCopy[index] = val;
    } else {
      dataCopy = val;
    }
    onChange(dataCopy);
  }
  return (
    <>
      {argumentTypes &&
        argumentTypes.map((type, i) => {
          if (typeof type === "string") {
            if (argumentSelectList) {
              return (
                <Box key={i}>
                  <ValueSelectFields
                    options={argumentSelectList}
                    value={(value?.[i] as string) || ""}
                    onChange={(val) => onChangeHandler(val, i)}
                    validationError={validationError}
                    isReadOnly={isReadOnly}
                    loading={isLoading}
                    isDisabled={isDisabled}
                  />
                </Box>
              );
            } else if (isArgumentMany(noOfArguments)) {
              return (
                <MultiValueInputFields
                  value={value ?? []}
                  filterValue={filterValue as string}
                  onChange={(val) => onChangeHandler(val, 0)}
                  isReadOnly={isReadOnly}
                  type={type}
                  activeErrorCheck={activeErrorCheck}
                  isDisabled={isDisabled}
                />
              );
            } else {
              return (
                <HStack key={i}>
                  {i === 1 && <Text>and</Text>}
                  <ValueInputFields
                    type={type}
                    value={!isBlank(value?.[i]) ? value[i] : ""}
                    onChange={(val) => onChangeHandler(val, i)}
                    validationError={validationError}
                    isReadOnly={isReadOnly}
                    isDisabled={isDisabled}
                  />
                </HStack>
              );
            }
          } else if (typeof type === "object") {
            return (
              <Box key={i}>
                <ValueSelectFields
                  options={type.map((typ) => ({ label: typ, value: typ }))}
                  value={(value?.[i] as string) || ""}
                  onChange={(val) => onChangeHandler(val, i)}
                  validationError={validationError}
                  isReadOnly={isReadOnly}
                  isDisabled={isDisabled}
                />
              </Box>
            );
          } else {
            throw new Error("Uknown Argument Type: " + type);
          }
        })}
      {!isReadOnly && helperText && (
        <IconWithTooltip
          label={isArray(helperText) ? helperText.join(", ") : helperText}
          color="gray.400"
          tooltipProps={{ fontSize: "xs" }}
        />
      )}
    </>
  );
}

function ValueBadge({ value }: { value: string | number }) {
  return (
    <Text
      ml={1}
      borderRadius="sm"
      color="white"
      backgroundColor="gray.400"
      maxW="100px"
      px={1}
      isTruncated
    >
      {value}
    </Text>
  );
}

export function ValueFieldsReadonly({
  value,
  argumentTypes,
  isMany,
  limit,
}: {
  value: ValueTypes;
  argumentTypes: (string | string[])[] | null;
  isMany?: boolean;
  limit?: number;
}) {
  if (isMany) {
    return (
      <>
        {value.slice(0, limit).map((val, index) => {
          return <ValueBadge value={`${val}`} key={index} />;
        })}
        {limit && value.length > limit ? <ValueBadge value={"..."} /> : ""}
      </>
    );
  } else
    return (
      <>
        {argumentTypes &&
          argumentTypes.map((type, i) => {
            if (typeof type === "string") {
              return (
                <>
                  {i !== 0 && " and"}
                  <ValueBadge value={`${value[i]}`} key={i} />
                </>
              );
            } else if (typeof type === "object") {
              return <ValueBadge value={value[i]} key={i} />;
            } else {
              throw new Error("Uknown Argument Type: " + type);
            }
          })}
      </>
    );
}
